// src/pages/client/PrivacyPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  FileText,
  Shield,
  CheckCircle,
  AlertCircle,
  Clock,
  Heart,
  Users,
  Lock,
  Eye,
  Database, 
  Cookie,
  Mail,
  Phone,
  MapPin,
  Globe,
  Award,
  ThumbsUp,
  Loader
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import useTranslation from '../../hooks/useTranslation';
import colors from '../../constants/colors';
import styles from '../../styles/PrivacyPage.module.css';
import {
  getPrivacyPolicy,
  acceptTermsAndPrivacy,
  checkUserAcceptance,
  selectPrivacyPolicy,
  selectUserAcceptance,
  selectSettingsLoading,
  selectSettingsProcessing,
  selectSettingsError,
  selectSettingsProcessSuccess,
  clearSettingsErrors,
  clearSettingsSuccess
} from '../../store/slices/platformSettingsSlice';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { userId } = useParams();
  
  // ======================================================
  // LOCAL STATE
  // ======================================================
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [hasAttemptedAccept, setHasAttemptedAccept] = useState(false);

  // ======================================================
  // CONTEXT & HOOKS
  // ======================================================
  const { currentLanguage } = useLanguage();

  // ======================================================
  // REDUX STATE
  // ======================================================
  const privacyPolicy = useSelector(selectPrivacyPolicy);
  const userAcceptance = useSelector(selectUserAcceptance);
  const loading = useSelector(selectSettingsLoading);
  const processing = useSelector(selectSettingsProcessing);
  const error = useSelector(selectSettingsError);
  const processSuccess = useSelector(selectSettingsProcessSuccess);

  // ======================================================
  // TRANSLATIONS
  // ======================================================
  const { translatedText: privacyPolicyTitle } = useTranslation('Privacy Policy');
  const { translatedText: lastUpdated } = useTranslation('Last Updated');
  const { translatedText: pleaseAcceptPrivacy } = useTranslation('Please accept the Privacy Policy before continuing.');
  const { translatedText: acceptedSuccess } = useTranslation('Accepted Successfully');
  const { translatedText: thankYouAccept } = useTranslation('Thank you for accepting our privacy policy.');
  const { translatedText: loadingText } = useTranslation('Loading...');
  const { translatedText: errorOccurred } = useTranslation('An error occurred');
  const { translatedText: tryAgain } = useTranslation('Try Again');
  const { translatedText: alreadyAccepted } = useTranslation('You have already accepted the latest privacy policy');
  const { translatedText: acceptPrivacy } = useTranslation('Accept Privacy Policy');
  const { translatedText: iHaveReadPrivacy } = useTranslation('I have read and agree to the Privacy Policy');
  const { translatedText: version } = useTranslation('Version');

  // ======================================================
  // FOOTER TRANSLATIONS
  // ======================================================
  const { translatedText: homeTitle } = useTranslation('Haitian Services Platform');
  const { translatedText: connectingHaitians } = useTranslation('Connecting Haitians Worldwide, One Service at a Time');
  const { translatedText: madeWithLove } = useTranslation('All rights reserved');
  const { translatedText: quickLinks } = useTranslation('Quick Links');
  const { translatedText: findProviders } = useTranslation('Find Providers');
  const { translatedText: supportUs } = useTranslation('Support Us');
  const { translatedText: helpAndSupport } = useTranslation('Help & Support');
  const { translatedText: aboutUs } = useTranslation('About Us');
  const { translatedText: forProviders } = useTranslation('For Providers');
  const { translatedText: becomeAProvider } = useTranslation('Become a Provider');
  const { translatedText: providerLogin } = useTranslation('Provider Login');
  const { translatedText: dashboard } = useTranslation('Dashboard');
  const { translatedText: settings } = useTranslation('Settings');
  const { translatedText: legal } = useTranslation('Legal');
  const { translatedText: termsOfService } = useTranslation('Terms of Service');
  const { translatedText: privacyPolicyLink } = useTranslation('Privacy Policy');
  const { translatedText: cookiePolicy } = useTranslation('Cookie Policy');

  // ======================================================
  // EFFECTS
  // ======================================================
  
  // Load privacy policy on mount
  useEffect(() => {
    loadPrivacyPolicy();
  }, [currentLanguage]);

  // Check user acceptance status
  useEffect(() => {
    if (userId) {
      dispatch(checkUserAcceptance(userId));
    }
  }, [userId, dispatch]);

  // Handle successful acceptance
  useEffect(() => {
    if (processSuccess && hasAttemptedAccept) {
      alert(`${acceptedSuccess}\n\n${thankYouAccept}`);
      dispatch(clearSettingsSuccess());
      setHasAttemptedAccept(false);
      setAcceptedPrivacy(false);
      
      // Refresh acceptance status
      if (userId) {
        dispatch(checkUserAcceptance(userId));
      }
      
      // Navigate back if callback exists
      navigate(-1);
    }
  }, [processSuccess, hasAttemptedAccept]);

  // Handle errors
  useEffect(() => {
    if (error) {
      alert(`${errorOccurred}\n\n${error}`);
      dispatch(clearSettingsErrors());
    }
  }, [error]);

  // ======================================================
  // FUNCTIONS
  // ======================================================
  
  const loadPrivacyPolicy = () => {
    dispatch(getPrivacyPolicy(currentLanguage));
  };

  const handleAcceptToggle = () => {
    setAcceptedPrivacy(!acceptedPrivacy);
  };

  const handleAccept = () => {
    if (!acceptedPrivacy) {
      alert(pleaseAcceptPrivacy);
      return;
    }

    if (!userId) {
      alert('User not authenticated. Please log in to accept the privacy policy.');
      return;
    }

    // Check if already accepted
    if (userAcceptance?.hasAccepted) {
      alert(alreadyAccepted);
      return;
    }

    const privacyVersion = privacyPolicy?.version || '1.0.0';

    setHasAttemptedAccept(true);
    dispatch(acceptTermsAndPrivacy({
      userId,
      termsVersion: '1.0.0', // Use actual terms version if available
      privacyVersion
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ======================================================
  // RENDER FUNCTIONS
  // ======================================================
  
  const renderContent = () => {
    if (loading && !privacyPolicy) {
      return (
        <div className={styles.loadingContainer}>
          <Loader size={48} className={styles.spinner} />
          <p>{loadingText}</p>
        </div>
      );
    }

    if (!privacyPolicy || !privacyPolicy.content) {
      return (
        <div className={styles.emptyContainer}>
          <FileText size={60} color={colors.gray} />
          <p>No privacy policy content available</p>
          <button className={styles.retryButton} onClick={loadPrivacyPolicy}>
            {tryAgain}
          </button>
        </div>
      );
    }

    const sections = privacyPolicy.content.sections || [];

    return (
      <>
        {/* Header Info */}
        <div className={styles.infoSection}>
          <h5 className={styles.infoTitle}>{privacyPolicy.title}</h5>
          <p className={styles.infoDate}>
            {lastUpdated}: {formatDate(privacyPolicy.lastUpdated)}
          </p>
          <p className={styles.infoVersion}>
            {version}: {privacyPolicy.version}
          </p>
        </div>

        {/* Sections - Dynamic from API */}
        {sections.map((section) => (
          <div key={section.order || section._id} className={styles.sectionContainer}>
            <div className={styles.sectionHeader}>
              <Shield size={20} color={colors.primary} />
              <h6 className={styles.sectionTitle}>{section.title}</h6>
            </div>
            <p className={styles.sectionContent}>{section.content}</p>
          </div>
        ))}

        {/* Compliance & Rights Info */}
        {privacyPolicy.compliance && (
          <div className={styles.complianceSection}>
            <h6 className={styles.complianceTitle}>Compliance & Data Protection</h6>
            <div className={styles.complianceGrid}>
              {privacyPolicy.compliance.gdprCompliant && (
                <div className={styles.complianceBadge}>
                  <CheckCircle size={16} color={colors.success} />
                  <span>GDPR Compliant</span>
                </div>
              )}
              {privacyPolicy.compliance.cookieConsentRequired && (
                <div className={styles.complianceBadge}>
                  <Cookie size={16} color={colors.primary} />
                  <span>Cookie Consent Required</span>
                </div>
              )}
              {privacyPolicy.compliance.dataRetentionPeriod && (
                <div className={styles.complianceBadge}>
                  <Clock size={16} color={colors.warning} />
                  <span>Data Retention: {privacyPolicy.compliance.dataRetentionPeriod} days</span>
                </div>
              )}
            </div>
            {privacyPolicy.compliance.dataProcessingAgreement && (
              <p className={styles.complianceText}>
                {privacyPolicy.compliance.dataProcessingAgreement}
              </p>
            )}
          </div>
        )}

        {/* User Rights */}
        {privacyPolicy.userRights && (
          <div className={styles.rightsSection}>
            <h6 className={styles.rightsTitle}>Your Rights</h6>
            <div className={styles.rightsGrid}>
              {privacyPolicy.userRights.rightToAccess && (
                <div className={styles.rightBadge}>Right to Access</div>
              )}
              {privacyPolicy.userRights.rightToRectification && (
                <div className={styles.rightBadge}>Right to Rectification</div>
              )}
              {privacyPolicy.userRights.rightToErasure && (
                <div className={styles.rightBadge}>Right to Erasure</div>
              )}
              {privacyPolicy.userRights.rightToObject && (
                <div className={styles.rightBadge}>Right to Object</div>
              )}
              {privacyPolicy.userRights.rightToDataPortability && (
                <div className={styles.rightBadge}>Right to Data Portability</div>
              )}
            </div>
          </div>
        )}

        {/* Acceptance Section - Only show if user is logged in */}
        {userId && (
          <div className={styles.acceptanceContainer}>
            <div className={styles.acceptanceRow}>
              <label className={styles.acceptanceLabel}>
                <input
                  type="checkbox"
                  checked={acceptedPrivacy}
                  onChange={handleAcceptToggle}
                  disabled={processing || userAcceptance?.hasAccepted}
                  className={styles.acceptanceCheckbox}
                />
                <span className={styles.acceptanceText}>{iHaveReadPrivacy}</span>
              </label>
            </div>

            {userAcceptance?.hasAccepted ? (
              <div className={styles.acceptedBadge}>
                <CheckCircle size={20} color={colors.success} />
                <span className={styles.acceptedBadgeText}>
                  Accepted on {formatDate(userAcceptance.acceptedAt)}
                </span>
              </div>
            ) : (
              <button
                className={`${styles.acceptButton} ${(!acceptedPrivacy || processing) ? styles.acceptButtonDisabled : ''}`}
                onClick={handleAccept}
                disabled={!acceptedPrivacy || processing}
              >
                {processing ? (
                  <>
                    <Loader size={20} className={styles.spinner} />
                    Processing...
                  </>
                ) : (
                  acceptPrivacy
                )}
              </button>
            )}

            {userAcceptance && !userAcceptance.hasAccepted && userAcceptance.requireReAcceptance && (
              <p className={styles.reAcceptanceText}>
                ⚠️ Please accept the latest version of our Privacy Policy
              </p>
            )}
          </div>
        )}
      </>
    );
  };

  // ======================================================
  // RENDER
  // ======================================================
  
  return (
    <div className={styles.privacyContainer}>
      {/* Header */}
      <div className={styles.privacyHeader}>
        <div className={styles.headerContent}>
          <button 
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={24} color={colors.white} />
          </button>
          <span className={styles.headerTitle}>{privacyPolicyTitle}</span>
        </div>
      </div>

      {/* Content */}
      <div className={styles.scrollContent}>
        <div className={styles.container}>
          {renderContent()}
        </div>
      </div>

      {/* Footer - FULLY TRANSLATED */}
      <footer className={styles.footerFull}>
        <div className={styles.footerContent}>
          <div className={styles.footerGridFull}>
            {/* Brand Column */}
            <div className={styles.footerBrandColumn}>
              <div className={styles.footerBrandContainer}>
                <div className={styles.footerLogoWrapper}>
                  <img 
                    src="/logo/logo_hsm.png" 
                    alt="Haitian Services"
                    className={styles.footerLogo}
                  />
                </div>
                <div>
                  <div className={styles.footerBrandName}>{homeTitle}</div>
                  <div className={styles.footerBrandDescription}>{connectingHaitians}</div>
                </div>
              </div>
              <div className={styles.footerSocialLinks}>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-facebook.svg" alt="Facebook" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-instagram.svg" alt="Instagram" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-twitter.svg" alt="Twitter" width="20" height="20" />
                </a>
                <a href="#" target="_blank" rel="noopener noreferrer" className={styles.footerSocialIcon}>
                  <img src="https://s.magecdn.com/social/tc-youtube.svg" alt="YouTube" width="20" height="20" />
                </a>
              </div>
            </div>

            {/* Quick Links - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{quickLinks}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/providers">{findProviders}</a></li>
                <li><a href="/donation">{supportUs}</a></li>
                <li><a href="/help-support">{helpAndSupport}</a></li>
                <li><a href="/about">{aboutUs}</a></li>
              </ul>
            </div>

            {/* For Providers - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{forProviders}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/register">{becomeAProvider}</a></li>
                <li><a href="/login">{providerLogin}</a></li>
                <li><a href="/provider-dashboard">{dashboard}</a></li>
                <li><a href="/provider-settings">{settings}</a></li>
              </ul>
            </div>

            {/* Legal - TRANSLATED */}
            <div className={styles.footerLinksColumn}>
              <h3 className={styles.footerHeading}>{legal}</h3>
              <ul className={styles.footerLinks}>
                <li><a href="/terms">{termsOfService}</a></li>
                <li><a href="/privacy">{privacyPolicyLink}</a></li>
                <li><a href="/cookies">{cookiePolicy}</a></li>
              </ul>
            </div>
          </div>

          {/* Footer Bottom */}
          <div className={styles.footerDividerFull}></div>
          <div className={styles.footerBottomFull}>
            <div className={styles.footerFlag}>
              <div className={styles.flagStripes}>
                <div className={`${styles.stripe} ${styles.stripeBlue}`}></div>
                <div className={`${styles.stripe} ${styles.stripeRed}`}></div>
              </div>
            </div>
            <div className={styles.footerBottomText}>
              © 2026 {homeTitle}, {madeWithLove}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;